import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';

const ORANGE = '#FF6B00';
const DARK = '#1A1A1A';
const MUTED = '#555';
const SOFT = '#F8F9FA';

const milestones = [
  {
    year: '1971',
    title: 'Machine Tools Trading',
    desc: 'Launched a machine tools trading company, supporting India’s growing manufacturing sector and building deep insights into industrial engineering.',
  },
  {
    year: '1989',
    title: 'Roll Forming & Steel Processing',
    desc: 'Started manufacturing roll forming machines and expanded into cut-to-length and slitting lines, strengthening capabilities in steel processing technology.',
  },
  {
    year: '1998',
    title: 'Sheet Metal Fabrication',
    desc: 'Established a sheet metal fabrication company known for best-in-class, highly accurate, and exceptionally complex sheet metal profiles.',
  },
  {
    year: 'Today',
    title: 'Two Manufacturing Units',
    desc: 'Operating state-of-the-art facilities in Rajkot and Umbergaon — including a 35,000 sq. ft. plant with seven in-house roll forming lines.',
  },
];

const processSteps = [
  'Concept',
  'Structural Design',
  'FEA Analysis',
  'Section Optimization',
  'Manufacturing',
  'Quality Validation',
  'Delivery',
];

const technicalCommitments = [
  'Structural integrity',
  'Material consistency',
  'Dimensional precision',
  'Reliability under operational loads',
];

function SectionHeading({ overline, title, subtitle }) {
  return (
    <Box sx={{ mb: { xs: 3, md: 4 } }}>
      {overline ? (
        <Typography sx={{ color: ORANGE, fontWeight: 900, letterSpacing: 3, fontSize: '0.78rem', mb: 1, textTransform: 'uppercase' }}>
          {overline}
        </Typography>
      ) : null}
      <Typography sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.65rem', md: '2.2rem' }, lineHeight: 1.2, mb: subtitle ? 1 : 0 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ color: MUTED, lineHeight: 1.75, fontSize: { xs: '1rem', md: '1.05rem' }, maxWidth: 820 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function About() {
  return (
    <Box sx={{ bgcolor: '#fff', overflowX: 'hidden' }}>
      <Box
        sx={{
          py: { xs: 8, md: 11 },
          bgcolor: '#fff',
          borderBottom: '1px solid #ededed',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(520px 240px at 12% 18%, rgba(255,107,0,0.1), transparent), linear-gradient(90deg, rgba(17,17,17,0.05) 1px, transparent 1px), linear-gradient(0deg, rgba(17,17,17,0.05) 1px, transparent 1px)',
            backgroundSize: 'auto, 34px 34px, 34px 34px',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}>
          <Box component={motion.div} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <Typography sx={{ color: ORANGE, fontWeight: 900, letterSpacing: 3, mb: 1, textTransform: 'uppercase', fontSize: '0.78rem' }}>
              About Us
            </Typography>
            <Typography sx={{ fontWeight: 900, color: DARK, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.1, maxWidth: 760 }}>
              Precision Engineering for High-Performance Industrial Storage
            </Typography>
          </Box>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <SectionHeading overline="01" title="Who We Are" />
          <Paper
            elevation={0}
            sx={{
              border: '1px solid #e6e6e6',
              borderLeft: `4px solid ${ORANGE}`,
              borderRadius: 0,
              p: { xs: 2.5, md: 3.5 },
              bgcolor: SOFT,
            }}
          >
            <Typography sx={{ color: DARK, fontSize: { xs: '1.05rem', md: '1.2rem' }, lineHeight: 1.8, fontWeight: 500, fontStyle: 'italic' }}>
              “We are a precision roll-forming and structural section engineering company focused on developing
              high-performance industrial storage systems.”
            </Typography>
          </Paper>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: SOFT, borderTop: '1px solid #ededed' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <SectionHeading
            overline="02"
            title="Our Engineering Foundation"
            subtitle="From machine tools trading to warehouse transformation — our legacy is built on precision, innovation, and customer success."
          />
          <Typography sx={{ color: MUTED, lineHeight: 1.85, fontSize: { xs: '1rem', md: '1.05rem' }, mb: 4 }}>
            Our journey began in 1971 with the launch of a machine tools trading company, supporting India’s growing
            manufacturing sector. This early experience gave us deep insights into industrial engineering and sparked our
            passion for precision and innovation. In 1989, we started manufacturing roll forming machines, and soon after
            expanded into producing cut-to-length lines and slitting lines, strengthening our capabilities in steel
            processing technology. By 1998, we had established a sheet metal fabrication company, where we earned a
            reputation for producing best-in-class, highly accurate, and exceptionally complex sheet metal profiles.
            Backed by our own in-house roll tool designing software and manufacturing facilities, we are able to develop
            new designs faster, ensure unmatched accuracy, and stay one step ahead of the competition— proof that we know
            steel and sheet metal better than anyone. Today, we operate two state-of-the-art manufacturing units—one in
            Rajkot and another in Umbergaon. Our Umbergaon plant spans over 35,000 sq. ft. of manufacturing space and is
            equipped with seven in-house roll forming lines, enabling us to deliver the best, most efficient output to meet
            diverse and demanding customer needs. Leveraging over five decades of experience, cutting-edge infrastructure,
            and a relentless focus on innovation, we are now channeling our capabilities into customizable pallet rack
            solutions. From trading machine tools to transforming warehouses, our legacy is defined by precision,
            innovation, and an unwavering commitment to our customers’ success.
          </Typography>
          <Grid container spacing={2}>
            {milestones.map((item) => (
              <Grid item xs={12} sm={6} key={item.year}>
                <Paper elevation={0} sx={{ border: '1px solid #e4e4e4', borderRadius: 0, p: 2.25, height: '100%', bgcolor: '#fff' }}>
                  <Typography sx={{ color: ORANGE, fontWeight: 900, fontSize: '0.85rem', letterSpacing: '0.1em', mb: 0.75 }}>
                    {item.year}
                  </Typography>
                  <Typography sx={{ fontWeight: 800, color: DARK, mb: 0.75, fontSize: '1.02rem' }}>{item.title}</Typography>
                  <Typography sx={{ color: MUTED, lineHeight: 1.6, fontSize: '0.92rem' }}>{item.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#fff', borderTop: '1px solid #ededed' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <SectionHeading overline="03" title="Why We Entered Storage Systems" />
          <Paper
            elevation={0}
            sx={{
              border: '1px solid #e6e6e6',
              borderLeft: `4px solid ${ORANGE}`,
              borderRadius: 0,
              p: { xs: 2.5, md: 3.5 },
              bgcolor: '#fff',
            }}
          >
            <Typography sx={{ color: DARK, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.8, fontWeight: 500, fontStyle: 'italic' }}>
              “Through years of structural steel section development, we recognized an opportunity to improve conventional
              storage systems through better section design, stronger profiles, and more efficient material engineering.”
            </Typography>
          </Paper>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: SOFT, borderTop: '1px solid #ededed' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <SectionHeading overline="04" title="Vision & Mission" />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ border: '1px solid #e4e4e4', borderTop: `4px solid ${ORANGE}`, borderRadius: 0, p: { xs: 2.5, md: 3 }, height: '100%', bgcolor: '#fff' }}>
                <Typography sx={{ fontWeight: 900, color: DARK, mb: 1.5, fontSize: { xs: '1.3rem', md: '1.5rem' } }}>Vision</Typography>
                <Typography sx={{ color: MUTED, lineHeight: 1.8, fontSize: '1rem' }}>
                  To be the benchmark for excellence in warehouse solutions—where innovation meets customization,
                  transforming warehouses into engines of productivity through forward-thinking design, unmatched quality,
                  and a relentless commitment to customer success.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ border: '1px solid #e4e4e4', borderTop: `4px solid ${ORANGE}`, borderRadius: 0, p: { xs: 2.5, md: 3 }, height: '100%', bgcolor: '#fff' }}>
                <Typography sx={{ fontWeight: 900, color: DARK, mb: 1.5, fontSize: { xs: '1.3rem', md: '1.5rem' } }}>Mission</Typography>
                <Typography sx={{ color: MUTED, lineHeight: 1.8, fontSize: '1rem' }}>
                  Our mission is to inspire confidence in every warehouse by designing and delivering customized storage
                  solutions that maximize space, streamline operations, and push the boundaries of innovation—combining
                  advanced engineering, flexible designs, and exceptional service to help our clients thrive and grow without
                  limits.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#fff', borderTop: '1px solid #ededed' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <SectionHeading overline="05" title="How We Work" subtitle="A structured engineering process from concept to delivery." />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(7, 1fr)' },
              gap: 1.25,
            }}
          >
            {processSteps.map((step, idx) => (
              <Box
                key={step}
                sx={{
                  border: '1px solid #e4e4e4',
                  bgcolor: idx % 2 ? SOFT : '#fff',
                  p: 1.5,
                  minHeight: 88,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ color: ORANGE, fontWeight: 900, fontSize: '0.75rem', mb: 0.5 }}>
                  {String(idx + 1).padStart(2, '0')}
                </Typography>
                <Typography sx={{ color: DARK, fontWeight: 700, fontSize: '0.92rem', lineHeight: 1.35 }}>{step}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: SOFT, borderTop: '1px solid #ededed' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <SectionHeading overline="06" title="Technical Commitment" />
          <Grid container spacing={2}>
            {technicalCommitments.map((item) => (
              <Grid item xs={12} sm={6} key={item}>
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <CheckCircleOutlineIcon sx={{ color: ORANGE, fontSize: 20, mt: 0.15, flexShrink: 0 }} />
                  <Typography sx={{ color: DARK, fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 }}>{item}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 7, md: 9 }, bgcolor: '#fff', borderTop: '1px solid #ededed' }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 }, textAlign: 'center' }}>
          <Typography sx={{ color: ORANGE, fontWeight: 900, letterSpacing: 3, mb: 1, textTransform: 'uppercase', fontSize: '0.78rem' }}>
            Get Started
          </Typography>
          <Typography sx={{ fontWeight: 900, color: DARK, mb: 1.5, fontSize: { xs: '1.6rem', md: '2rem' } }}>
            Discuss Your Storage Requirement
          </Typography>
          <Typography sx={{ color: MUTED, mb: 3, lineHeight: 1.7, maxWidth: 560, mx: 'auto' }}>
            Share your space, load, and operational goals — our team will recommend the right storage approach.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
            <Button
              component={RouterLink}
              to="/#requirement-form"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{ bgcolor: ORANGE, borderRadius: 0, fontWeight: 800, px: 4, '&:hover': { bgcolor: '#e66000' } }}
            >
              Fill Requirement Form
            </Button>
            <Button
              component={RouterLink}
              to="/contact"
              variant="outlined"
              size="large"
              sx={{ borderRadius: 0, fontWeight: 800, color: DARK, borderColor: '#cfcfcf', px: 4, '&:hover': { borderColor: ORANGE, color: ORANGE } }}
            >
              Contact Us
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
